/**
 * Mojo Up AI Cloud → back from Stripe (`/billing/return?organisation=<id>`). Stripe Checkout and the
 * Stripe customer portal both send the browser here. Cloud asks Mojo Up billing for the team's
 * entitlement again, and waits a little while Stripe confirms, so the plan shown is the new one.
 */

import type { CloudBilling } from '@aioe/shared';
import { dateLong } from '@aioe/shared/wording/dateTime';
import { CreditCard } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { problemText, routes } from '../../api/client.ts';
import { useCloud } from '../../auth/AuthProvider.tsx';
import { billingStatus } from '../../components/cloud/Billing.tsx';
import { ArtEmpty, ErrorNote, Page, PageHeader, StatusMark } from '../../components/ui.tsx';
import { VIEW_TONES } from '../../components/ui/Art.tsx';

const tone = VIEW_TONES.billing!;
const longDay = (iso: string) => dateLong(iso);
/** Stripe's webhook usually lands within seconds; ask a few times before saying it is still on its way. */
const TRIES = 6;
const PAUSE_MS = 2000;

export function BillingReturnPage() {
  const cloud = useCloud()!;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const wanted = params.get('organisation') ?? cloud.organisation.id;
  const team = cloud.me.organisations.find((o) => o.id === wanted);
  const [billing, setBilling] = useState<CloudBilling>();
  const [waiting, setWaiting] = useState(true);
  const [error, setError] = useState<string>();

  // Back from Stripe in the team that was paying for, even if the switcher was elsewhere.
  useEffect(() => {
    if (team && cloud.organisation.id !== team.id) cloud.switchTo(team.id);
  }, [team, cloud]);

  useEffect(() => {
    if (!team) return;
    let stop = false;
    void (async () => {
      for (let i = 0; i < TRIES && !stop; i++) {
        try {
          const b = await routes.refreshBilling(team.id);
          if (stop) return;
          setBilling(b);
          if (b.plan === 'teams' || b.status !== 'none') break;
        } catch (e) {
          if (!stop) setError(problemText(e));
          break;
        }
        await new Promise((r) => setTimeout(r, PAUSE_MS));
      }
      if (!stop) {
        setWaiting(false);
        await cloud.reload().catch(() => undefined);
      }
    })();
    return () => {
      stop = true;
    };
    // Once per arrival: reloading the person must not start the polling again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.id]);

  const status = billing ? billingStatus(billing) : undefined;
  const headline = !billing
    ? error
      ? 'Billing could not be checked'
      : 'Checking with billing'
    : billing.status === 'trialing'
      ? `Your free trial of Teams has started${billing.trialEnd ? ` and runs to ${longDay(billing.trialEnd)}` : ''}`
      : billing.plan === 'teams'
        ? `${team?.name ?? 'Your team'} is on Teams, with ${billing.seatLimit} ${billing.seatLimit === 1 ? 'seat' : 'seats'}`
        : waiting
          ? 'Waiting for Stripe to confirm'
          : 'Stripe has not confirmed a subscription yet';

  return (
    <Page narrow>
      <PageHeader tone={tone} icon={<CreditCard />} title="Billing" subtitle={team ? `Teams for ${team.name}` : 'Back from Stripe'} />
      {!team ? (
        <ArtEmpty tone={tone} icon={<CreditCard />} title="That team is not one of yours" action={<Button size="sm" variant="primary" onPress={() => navigate('/team', { replace: true })}>Go to your team</Button>}>
          Switch to the team you were paying for, then open Team to see its billing.
        </ArtEmpty>
      ) : (
        <ArtEmpty
          tone={tone}
          icon={<CreditCard />}
          title={headline}
          action={
            <Button size="sm" variant="primary" isDisabled={waiting} onPress={() => navigate('/team', { replace: true })}>
              Back to the team
            </Button>
          }
        >
          {status ? <StatusMark state={status.state} label={status.label} /> : error ? 'If you finished paying, the plan shows on the Team page once billing answers. If you left Stripe early, nothing was charged.' : 'This takes a few seconds.'}
          {!waiting && billing && billing.plan !== 'teams' && billing.status === 'none' ? ' If you finished paying, it will show on the Team page within a minute. If you left Stripe early, nothing was charged.' : ''}
        </ArtEmpty>
      )}
      <ErrorNote error={error} />
    </Page>
  );
}
